import isPlainObject from 'lodash/isPlainObject';
import type MapProps from '../types/MapProps';

/**
 * A function for mapping props to values on the row param.
 *
 * @typedef {Function} mapPropsFn
 * @param {Object} row
 * @returns {Object}
 */

/**
 * Takes values from the `row` param and converts them into a new object,
 * using the `mapProps` param as a guide. The purpose of this function is
 * convert the values of the `row` param into a new object that can be used
 * as props for a <DataList> column.
 *
 * Using an object for `mapProps`, it can create a new object which has
 * mapped the values from `row` to the shape of the `mapProps` param.
 *
 * @example
 * // returns { bar: 'foo' }
 * mapRenderProps({ foo: 'foo', { bar: 'foo' });
 *
 * Passing in `mapProps` as a function allows for more control over the
 * returned value from the function.
 *
 * @example
 * // returns { foobar: 3 }
 * mapRenderProps({ foo: 1, bar: 2, (row) => ({ foobar: row.foo + row.bar }));
 *
 * @param {Object} row
 * @param {Object|mapPropsFn} mapProps
 * @returns {Object}
 */
export default function mapRenderProps<DataType>(
  row: DataType,
  mapProps: MapProps<DataType>,
): {
  [k: string]: unknown;
} {
  let renderProps: {
    [k: string]: unknown;
  } = {};

  if (typeof mapProps === 'function') {
    const mappedValues = mapProps(row);

    if (isPlainObject(mappedValues)) {
      renderProps = {
        ...renderProps,
        ...(mappedValues as object),
      };
    }
  } else {
    Object.keys(mapProps).forEach((propKey) => {
      const propValue = mapProps[propKey];

      if (row[propValue]) {
        renderProps[propKey] = row[propValue];
      }
    });
  }

  return renderProps;
}
